import { catalog } from './catalog.js'
import { memberCompletion, pathHover } from './js-source.js'
import type { Completion, CompletionContext, CompletionResult, HoverInfo, SignatureHelp } from './types.js'
import { scanLine, wordBefore, openersBefore, calleeBefore, topLevelCommas, firstStringArg, wordAt, stringAt } from './text.js'

/**
 * Python completions for a PDApp's server scripts (`_api/*.py`, hooks, schedules) —
 * the `papan_sandbox` helpers the sandbox installs:
 *
 *   from papan_sandbox import |     → every helper
 *   papan_sandbox.|                 → the same
 *   emi|                            → the helpers this file imported (all of them with `*`)
 *   db_run('|                       → the app's op files
 *   db_run('_db/x.sql', {'|         → the op's `:params`
 *   actor['|  env['|  app['|        → the keys the sandbox envelope carries
 */

const MODULE = 'papan_sandbox'

/** The envelope keys a script can read (features/pdapp-runtime/server/sandbox-envelope.ts). */
const ENVELOPE_KEYS: Record<string, Array<[string, string]>> = {
  actor: [
    ['id', 'Pairwise actor id — always present; the same id `@actor` binds in SQL.'],
    ['is_personal', 'True for a personal account, False when acting as an org member.'],
    ['consented', 'The consent keys the person granted.'],
    ['email', 'Consent: email.'],
    ['email_verified', 'Consent: email.'],
    ['name', 'Consent: name.'],
    ['role', 'Consent: role — the organization membership role.'],
    ['org_id', 'Consent: org — the acting org id (None when personal).'],
    ['org_name', 'Consent: org — the acting org name (None when personal).'],
    ['teams', 'Consent: teams — nested `{team_id: {name, subteams}}`.'],
  ],
  env: [
    ['args', 'The args passed to `scripts.run(path, args)`.'],
    ['actor', 'The invoking person (empty for an app hook).'],
    ['app', 'The app principal (None in Mode A).'],
  ],
  app: [
    ['tenant_id', 'The app folder\'s owner tenant.'],
    ['folder_id', 'The app folder id.'],
    ['name', 'From the manifest.'],
    ['version', 'From the manifest.'],
  ],
}

/** Helper names this file can call bare: those imported from papan_sandbox (all with `*`). */
export function importedHelpers(doc: string): Set<string> | 'all' {
  const names = new Set<string>()
  for (const m of doc.matchAll(/^\s*from\s+papan_sandbox\s+import\s+(\([^)]*\)|[^\n#]+)/gm)) {
    const list = m[1]!.replace(/[()]/g, '')
    if (/\*/.test(list)) return 'all'
    for (const part of list.split(',')) {
      const name = part.trim().split(/\s+as\s+/)[0]!.trim()
      if (name) names.add(name)
    }
  }
  return names
}

const helperCompletions = (filter?: Set<string> | 'all'): Completion[] =>
  catalog.python
    .filter((m) => filter === undefined || filter === 'all' || filter.has(m.name))
    .map(memberCompletion)

export async function pythonCompletions(ctx: CompletionContext): Promise<CompletionResult | null> {
  const { doc, pos } = ctx
  const line = scanLine(doc, pos, '#')
  if (line.comment) return null
  const lineStart = doc.lastIndexOf('\n', pos - 1) + 1

  // ── Inside a string ─────────────────────────────────────────────────
  if (line.stringStart !== null) {
    const before = doc.slice(Math.max(0, line.stringStart - 300), line.stringStart)
    const from = line.stringStart + 1
    // actor['|   env["|   app.get('|
    const sub = before.match(/\b(actor|env|app)\s*(?:\[|\.get\(\s*)$/)
    if (sub) {
      return { from, options: ENVELOPE_KEYS[sub[1]!]!.map(([label, info]) => ({ label, type: 'property' as const, info })) }
    }
    const head = before.replace(/\(\s*$/, '')
    const callee = calleeBefore(head, head.length)
    if (/\(\s*$/.test(before) && /(^|\.)(db_run|db_run_cost)$/.test(callee)) {
      return { from, options: (await ctx.app?.opPaths() ?? []).map((p) => ({ label: p, type: 'file' as const, detail: 'op' })) }
    }
    // db_run('_db/x.sql', {'|  — a dict key
    const openers = openersBefore(doc, line.stringStart, '#', false)
    const inner = openers[openers.length - 1]
    const call = openers[openers.length - 2]
    if (inner?.ch === '{' && call?.ch === '(' && /(^|\.)(db_run|db_run_cost)$/.test(calleeBefore(doc, call.at))) {
      const argsText = doc.slice(call.at + 1, inner.at)
      const objectText = doc.slice(inner.at, line.stringStart)
      const op = firstStringArg(argsText)
      if (op && ctx.app && topLevelCommas(argsText) === 1 && /(?:^\{|,)\s*$/.test(objectText)) {
        return { from, options: (await ctx.app.opParams(op)).map((p) => ({ label: p, type: 'property' as const, detail: `:${p}` })) }
      }
    }
    return null
  }

  const word = wordBefore(doc, pos, /\w/)
  const wordStart = pos - word.length
  const lineHead = doc.slice(lineStart, wordStart)

  if (/^\s*from\s+papan_sandbox\s+import\s+(?:\(?[\w\s,]*)$/.test(lineHead)) {
    const taken = importedHelpers(doc.slice(0, lineStart) + lineHead)
    return { from: wordStart, options: helperCompletions().filter((c) => taken === 'all' || !taken.has(c.label)) }
  }
  if (/^\s*from\s+$/.test(lineHead) || /^\s*import\s+$/.test(lineHead)) {
    return { from: wordStart, options: [{ label: MODULE, type: 'namespace', detail: 'PDApp sandbox helpers' }] }
  }
  if (/\bpapan_sandbox\s*\.\s*$/.test(lineHead)) {
    return { from: wordStart, options: helperCompletions() }
  }
  if (/\.\s*$/.test(lineHead)) return null
  if (!ctx.explicit && word.length < 2) return null
  const imported = importedHelpers(doc)
  const options = helperCompletions(ctx.explicit ? undefined : imported)
  if (ctx.explicit && imported !== 'all') {
    // Offered even when not imported yet — say so.
    for (const o of options) if (!imported.has(o.label)) o.detail = `${o.detail ?? ''}  · from papan_sandbox`.trim()
  }
  return { from: wordStart, options }
}

export function pythonSignature(ctx: CompletionContext): SignatureHelp | null {
  const { doc, pos } = ctx
  if (scanLine(doc, pos, '#').comment) return null
  const openers = openersBefore(doc, pos, '#', false)
  for (let i = openers.length - 1; i >= 0; i--) {
    const o = openers[i]!
    if (o.ch !== '(') continue
    const name = calleeBefore(doc, o.at).replace(/^papan_sandbox\./, '')
    const m = catalog.python.find((h) => h.name === name && h.kind === 'method')
    return m ? { signature: m.signature, doc: m.doc } : null
  }
  return null
}

/** What hovering a name in a Python script shows: the papan_sandbox helpers, envelope keys, op / script paths. */
export async function pythonHover(ctx: CompletionContext): Promise<HoverInfo | null> {
  const { doc, pos } = ctx
  const str = stringAt(doc, pos, '#')
  if (str) {
    const sub = doc.slice(Math.max(0, str.from - 40), str.from - 1).match(/\b(actor|env|app)\s*(?:\[|\.get\(\s*)$/)
    const key = sub && ENVELOPE_KEYS[sub[1]!]!.find(([k]) => k === str.text)
    if (key) return { from: str.from, to: str.to, title: `${sub![1]}["${key[0]}"]`, doc: key[1] }
    return pathHover(str.text, str.from, str.to, ctx.app)
  }
  if (scanLine(doc, pos, '#').comment) return null
  const w = wordAt(doc, pos, /\w/)
  if (!w) return null
  if (w.word === MODULE) return { from: w.from, to: w.to, title: MODULE, doc: 'The helpers the PDApp sandbox installs for server scripts: events, db_run, the actor envelope, team/role checks.' }
  const m = catalog.python.find((h) => h.name === w.word)
  return m ? { from: w.from, to: w.to, title: m.signature, doc: m.doc } : null
}
